import { apiFetch } from '@/lib/api';
import type {
  CadePartyList,
  CadePartyProfile,
  CadePartyQuery,
  CadeActivity,
  CadeAudioRecordingDetail,
  CadeAudioRecordingList,
  CadeAudioSearchResponse,
  CadeAudioTopics,
  CadeCaseAudio,
  CadeDossier,
  CadeJudicial,
  CadeSei,
  CadeCompany,
  CadeCompanySearch,
  CadeDocumentQuery,
  CadeDocuments,
  CadeJudicialLinkIn,
  CadeAudioTranscript,
  CadePositioning,
  CadeVistas,
  CadePautas,
  CadeRegister,
  CadeConsistency,
  CadeSanctions,
  CadeCaseDetail,
  CadeCaseList,
  CadeCommissioners,
  CadeConselheiro,
  CadeDecisionList,
  CadeConsistencyQuery,
  CadeDecisionQuery,
  CadeDecisionRecord,
  CadeFineLedger,
  CadeLedgerQuery,
  CadeOverview,
  CadeSessionList,
  CadeVotedDecisionList,
  CadeVotes,
  ConselhoBench,
  ConselhoItemMetric,
  ConselhoItems,
  ConselhoMemberItems,
} from '@/services/types';

/** The third agency. Mostly refusals, and that is the deliverable. */
export function fetchCadeOverview(): Promise<CadeOverview> {
  return apiFetch<CadeOverview>('/api/v1/cade/overview');
}

/**
 * CADE's published case universe by year -- the only CADE endpoint that returns
 * a coverage span, because it reads a different population from the rest.
 */
export function fetchCadeActivity(): Promise<CadeActivity> {
  return apiFetch<CadeActivity>('/api/v1/cade/activity');
}

/**
 * The published register's funnels: durations to the day, what the Tribunal
 * later did with the protocols the SG decided, appeal flags, TCC contributions
 * and CNAE sectors. The same population as the activity screen, read at
 * protocol grain.
 */
export function fetchCadeRegister(): Promise<CadeRegister> {
  return apiFetch<CadeRegister>('/api/v1/cade/register');
}

/**
 * Builds the consistency-queue query string.
 *
 * Exported because the CSV export must replay the SAME parameters as the
 * screen. `limit`/`offset` describe a page and are deliberately omitted.
 */
export function cadeConsistencyParams(q: CadeConsistencyQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (q.caseType) params.set('case_type', q.caseType);
  if (q.citation) params.set('citation', q.citation);
  return params;
}

/** The consistency queue: comparator candidates, never adjudicated verdicts. */
export function fetchCadeConsistency(q: CadeConsistencyQuery): Promise<CadeConsistency> {
  const params = cadeConsistencyParams(q);
  if (q.limit !== undefined) params.set('limit', String(q.limit));
  if (q.offset !== undefined) params.set('offset', String(q.offset));
  return apiFetch<CadeConsistency>(`/api/v1/cade/consistency?${params.toString()}`);
}

/** Fines as a distribution, the two cohorts, and who inferred the attributions. */
export function fetchCadeSanctions(): Promise<CadeSanctions> {
  return apiFetch<CadeSanctions>('/api/v1/cade/sanctions');
}

/**
 * The ledger's filters as the API reads them. Shared by the fetch and the
 * export, so the file is the screen's query; page bounds are deliberately not
 * here -- an export is the whole result set.
 */
export function cadeLedgerParams(q: CadeLedgerQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (q.outcome) params.set('outcome', q.outcome);
  if (q.sessionYear) params.set('session_year', String(q.sessionYear));
  if (q.caseType) params.set('case_type', q.caseType);
  if (q.minBrl !== undefined && q.minBrl !== null && q.minBrl > 0) {
    params.set('min_brl', String(q.minBrl));
  }
  if (q.onlyFinal) params.set('only_final', 'true');
  if (q.onlyAttributed) params.set('only_attributed', 'true');
  return params;
}

/** Every minuted amount, one row per fine row, largest first. */
export function fetchCadeFineLedger(q: CadeLedgerQuery): Promise<CadeFineLedger> {
  const params = cadeLedgerParams(q);
  if (q.limit !== undefined) params.set('limit', String(q.limit));
  if (q.offset !== undefined) params.set('offset', String(q.offset));
  const query = params.toString();
  return apiFetch<CadeFineLedger>(`/api/v1/cade/sanctions/ledger${query ? `?${query}` : ''}`);
}

export interface CadeCaseQuery {
  query?: string | null;
  caseType?: string | null;
  outcomeCodes?: string[];
  /** Defaults to true server-side: the registry rows carry no decision. */
  onlyAdjudicated?: boolean;
  /** Archive year of a session the case was decided in. */
  sessionYear?: number | null;
  /** One CNAE section code from CADE's register. */
  sector?: string | null;
  /** Fields coded from the votes (served values only): a conduct code, a UF, a market substring. */
  codedConduct?: string | null;
  codedUf?: string | null;
  codedMarket?: string | null;
  codedHarm?: string | null;
  codedRemedy?: string | null;
  codedSg?: string | null;
  codedRate?: string | null;
  codedTccMin?: number | null;
  codedTccMax?: number | null;
  /** Only processes whose SEI record holds a court decision. */
  courtInSei?: boolean;
  limit?: number;
  offset?: number;
}

/**
 * Builds the case-list query string.
 *
 * Exported because the CSV export must replay the SAME parameters as the screen.
 * Two separately assembled query strings is how a spreadsheet quietly stops
 * matching the page it came from.
 */
export function cadeCaseParams(q: CadeCaseQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (q.query) params.set('query', q.query);
  if (q.caseType) params.set('case_type', q.caseType);
  for (const code of q.outcomeCodes ?? []) params.append('outcome_codes', code);
  if (q.onlyAdjudicated !== undefined) params.set('only_adjudicated', String(q.onlyAdjudicated));
  if (q.sessionYear) params.set('session_year', String(q.sessionYear));
  if (q.sector) params.set('sector', q.sector);
  if (q.codedConduct) params.set('coded_conduct', q.codedConduct);
  if (q.codedUf) params.set('coded_uf', q.codedUf);
  if (q.codedMarket) params.set('coded_market', q.codedMarket);
  if (q.codedHarm) params.set('coded_harm', q.codedHarm);
  if (q.codedRemedy) params.set('coded_remedy', q.codedRemedy);
  if (q.codedSg) params.set('coded_sg', q.codedSg);
  if (q.codedRate) params.set('coded_rate', q.codedRate);
  if (q.codedTccMin != null) params.set('coded_tcc_min', String(q.codedTccMin));
  if (q.codedTccMax != null) params.set('coded_tcc_max', String(q.codedTccMax));
  if (q.courtInSei) params.set('court_in_sei', 'true');
  if (q.limit !== undefined) params.set('limit', String(q.limit));
  if (q.offset !== undefined) params.set('offset', String(q.offset));
  return params;
}

export function fetchCadeCases(q: CadeCaseQuery): Promise<CadeCaseList> {
  return apiFetch<CadeCaseList>(`/api/v1/cade/cases?${cadeCaseParams(q).toString()}`);
}

/** `key` is the digits-only case number; the canonical form carries a slash. */
export function fetchCadeCase(key: string): Promise<CadeCaseDetail> {
  return apiFetch<CadeCaseDetail>(`/api/v1/cade/cases/${encodeURIComponent(key)}`);
}

/**
 * Builds the decision-search query string.
 *
 * Exported because the CSV export must replay the SAME parameters as the screen.
 * `limit`/`offset` describe a page and are deliberately omitted by the export,
 * which takes the whole result set up to the row cap.
 */
export function cadeDecisionParams(q: CadeDecisionQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (q.query) params.set('query', q.query);
  if (q.relator) params.set('relator', q.relator);
  if (q.onlyWithText !== undefined) params.set('only_with_text', String(q.onlyWithText));
  if (q.caseType) params.set('case_type', q.caseType);
  if (q.track) params.set('track', q.track);
  if (q.outcomeCode) params.set('outcome_code', q.outcomeCode);
  if (q.scope) params.set('scope', q.scope);
  if (q.citation) params.set('citation', q.citation);
  if (q.sessionYear) params.set('session_year', String(q.sessionYear));
  if (q.onlyFinal) params.set('only_final', 'true');
  if (q.session) params.set('session', q.session);
  return params;
}

/** The session archive, one row per ata, newest archive year first. */
export function fetchCadeSessions(q: { limit?: number; offset?: number }): Promise<CadeSessionList> {
  const params = new URLSearchParams();
  if (q.limit !== undefined) params.set('limit', String(q.limit));
  if (q.offset !== undefined) params.set('offset', String(q.offset));
  const query = params.toString();
  return apiFetch<CadeSessionList>(`/api/v1/cade/sessions${query ? `?${query}` : ''}`);
}

/** One decision by its natural key: the votes as minuted and everything the item carries. */
export function fetchCadeVotedDecision(key: string): Promise<CadeDecisionRecord> {
  return apiFetch<CadeDecisionRecord>(`/api/v1/cade/votes/d/${encodeURIComponent(key)}`);
}

/** The decisions carrying a recorded vote -- the index of the decision pages. */
export function fetchCadeVotedDecisions(q: {
  limit?: number;
  offset?: number;
  sessionYear?: number | null;
}): Promise<CadeVotedDecisionList> {
  const params = new URLSearchParams();
  if (q.limit !== undefined) params.set('limit', String(q.limit));
  if (q.offset !== undefined) params.set('offset', String(q.offset));
  if (q.sessionYear) params.set('session_year', String(q.sessionYear));
  const query = params.toString();
  return apiFetch<CadeVotedDecisionList>(`/api/v1/cade/votes/decisions${query ? `?${query}` : ''}`);
}

/** Decision search over the minuted text, the rapporteur and the case number. */
export function fetchCadeDecisions(q: CadeDecisionQuery): Promise<CadeDecisionList> {
  const params = cadeDecisionParams(q);
  if (q.limit !== undefined) params.set('limit', String(q.limit));
  if (q.offset !== undefined) params.set('offset', String(q.offset));
  return apiFetch<CadeDecisionList>(`/api/v1/cade/decisions?${params.toString()}`);
}

/** Rapporteur caseloads. Counts only -- there is no rate on this endpoint. */
export function fetchCadeCommissioners(): Promise<CadeCommissioners> {
  return apiFetch<CadeCommissioners>('/api/v1/cade/commissioners');
}

/** Who was minuted together. Counts only -- the rate is refused in the payload. */
export function fetchCadeVotes(): Promise<CadeVotes> {
  return apiFetch<CadeVotes>('/api/v1/cade/votes');
}

export function fetchCadeConselho(): Promise<ConselhoBench> {
  return apiFetch<ConselhoBench>('/api/v1/cade/conselho');
}

/** The complete items behind one bench figure (follow, outvoted, or a pair). */
export function fetchCadeConselhoCases(params: {
  name: string;
  metric: ConselhoItemMetric;
  other?: string | null;
}): Promise<ConselhoMemberItems> {
  const query = new URLSearchParams({ name: params.name, metric: params.metric });
  if (params.other) query.set('other', params.other);
  return apiFetch<ConselhoMemberItems>(`/api/v1/cade/conselho/cases?${query.toString()}`);
}

export function fetchCadeConselhoItems(): Promise<ConselhoItems> {
  return apiFetch<ConselhoItems>('/api/v1/cade/conselho/items');
}

/** `slug` is the accent-folded canonical name, derived by the backend. */
export function fetchCadeConselheiro(slug: string): Promise<CadeConselheiro> {
  return apiFetch<CadeConselheiro>(`/api/v1/cade/conselheiros/${encodeURIComponent(slug)}`);
}

/**
 * Parties named on a CADE decision, searchable by name.
 *
 * The population is the SESSION ARCHIVE's 3,209 parties, not the 16,423 rows in
 * `parties` -- the rest come from CADE's published registry and are named on no
 * tribunal decision.
 */
export function fetchCadeParties(q: CadePartyQuery): Promise<CadePartyList> {
  const params = new URLSearchParams();
  if (q.query) params.set('q', q.query);
  params.set('limit', String(q.limit ?? 25));
  params.set('offset', String(q.offset ?? 0));
  return apiFetch<CadePartyList>(`/api/v1/cade/parties?${params.toString()}`);
}

/** `slug` is the accent-folded `name_canonical`, unique across all 16,423 rows. */
export function fetchCadeParty(slug: string): Promise<CadePartyProfile> {
  return apiFetch<CadePartyProfile>(`/api/v1/cade/parties/${encodeURIComponent(slug)}`);
}

/**
 * The newest pautas the archive holds, by the date each one prints. The archive is
 * loaded by hand: the payload carries the age of the newest session.
 */
export function fetchCadePautas(limit = 4): Promise<CadePautas> {
  return apiFetch<CadePautas>(`/api/v1/cade/pautas?limit=${limit}`);
}

export interface CadeAudioQuery {
  query: string;
  year?: number | null;
  limit?: number;
  offset?: number;
}

/** The audio search's filters; shared by the fetch and the export so the file is the screen's query. */
export function cadeAudioParams(q: CadeAudioQuery): URLSearchParams {
  const params = new URLSearchParams();
  params.set('query', q.query);
  if (q.year) params.set('year', String(q.year));
  return params;
}

/** Passages of the session recordings' machine transcripts, best first. */
export function fetchCadeAudioSearch(q: CadeAudioQuery): Promise<CadeAudioSearchResponse> {
  const params = cadeAudioParams(q);
  if (q.limit !== undefined) params.set('limit', String(q.limit));
  if (q.offset !== undefined) params.set('offset', String(q.offset));
  return apiFetch<CadeAudioSearchResponse>(`/api/v1/cade/audio/search?${params.toString()}`);
}

/** The transcribed recordings, newest first, with how many spoken events each holds. */
export function fetchCadeAudioRecordings(): Promise<CadeAudioRecordingList> {
  return apiFetch<CadeAudioRecordingList>('/api/v1/cade/audio/recordings');
}

/** The results, vote calls and recusals said aloud in one recording. */
export function fetchCadeAudioRecording(key: string): Promise<CadeAudioRecordingDetail> {
  return apiFetch<CadeAudioRecordingDetail>(
    `/api/v1/cade/audio/recordings/${encodeURIComponent(key)}`,
  );
}

/** The whole machine transcript of one recording, with the spoken-vote segments marked. */
export function fetchCadeAudioTranscript(key: string): Promise<CadeAudioTranscript> {
  return apiFetch<CadeAudioTranscript>(
    `/api/v1/cade/audio/recordings/${encodeURIComponent(key)}/transcript`,
  );
}

export interface CadePositioningQuery {
  conducts: string[];
  procedure?: string | null;
  amount?: number | null;
  tccAmount?: number | null;
  partiesMin?: number | null;
  partiesMax?: number | null;
  limit?: number;
}

/** The positioning filters; shared by the fetch and the export so the file is the screen's query. */
export function cadePositioningParams(q: CadePositioningQuery): URLSearchParams {
  const params = new URLSearchParams();
  for (const conduct of q.conducts) params.append('conduct', conduct);
  if (q.procedure) params.set('procedure', q.procedure);
  if (q.partiesMin != null) params.set('parties_min', String(q.partiesMin));
  if (q.partiesMax != null) params.set('parties_max', String(q.partiesMax));
  return params;
}

/** Register protocols like a matter, with where their fines fell. */
export function fetchCadePositioning(q: CadePositioningQuery): Promise<CadePositioning> {
  const params = cadePositioningParams(q);
  if (q.amount != null) params.set('amount', String(q.amount));
  if (q.tccAmount != null) params.set('tcc_amount', String(q.tccAmount));
  if (q.limit !== undefined) params.set('limit', String(q.limit));
  return apiFetch<CadePositioning>(`/api/v1/cade/positioning?${params.toString()}`);
}


export interface CadeAudioTopicsQuery {
  topic?: string | null;
  recording?: string | null;
  limit?: number;
  offset?: number;
}

/** Where a topic is mentioned in the recordings; without a topic, only the per-topic counts. */
export function fetchCadeAudioTopics(q: CadeAudioTopicsQuery): Promise<CadeAudioTopics> {
  const params = new URLSearchParams();
  if (q.topic) params.set('topic', q.topic);
  if (q.recording) params.set('recording', q.recording);
  if (q.limit !== undefined) params.set('limit', String(q.limit));
  if (q.offset !== undefined) params.set('offset', String(q.offset));
  const query = params.toString();
  return apiFetch<CadeAudioTopics>(`/api/v1/cade/audio/topics${query ? `?${query}` : ''}`);
}


/** What the recorded session said about one case, from the stretch its pauta item occupies. */
export function fetchCadeCaseAudio(key: string): Promise<CadeCaseAudio> {
  return apiFetch<CadeCaseAudio>(`/api/v1/cade/cases/${encodeURIComponent(key)}/audio`);
}

/** Items adjourned for a pedido de vista, with the delay and who asked. */
export function fetchCadeVistas(): Promise<CadeVistas> {
  return apiFetch<CadeVistas>('/api/v1/cade/vistas');
}

/** One case laid out for the day before its session (history, relator's record, register). */
export function fetchCadeDossier(key: string): Promise<CadeDossier> {
  return apiFetch<CadeDossier>(`/api/v1/cade/dossier/${encodeURIComponent(key)}`);
}

/** The court cases linked to one CADE process, each with the court's published record. */
/** Candidate companies for a name, or the one company a CNPJ or root names. */
export function fetchCadeCompanySearch(query: string): Promise<CadeCompanySearch> {
  return apiFetch<CadeCompanySearch>(`/api/v1/cade/company?query=${encodeURIComponent(query)}`);
}

/** Everything CADE's record links to one company (8-digit CNPJ root). */
export function fetchCadeCompany(root: string): Promise<CadeCompany> {
  return apiFetch<CadeCompany>(`/api/v1/cade/company/${encodeURIComponent(root)}`);
}

/** The query string the document search and its export share, so the two cannot differ. */
export function cadeDocumentParams(q: CadeDocumentQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (q.query) params.set('query', q.query);
  if (q.mode && q.mode !== 'hybrid') params.set('mode', q.mode);
  if (q.kind) params.set('kind', q.kind);
  if (q.year_from) params.set('year_from', String(q.year_from));
  if (q.year_to) params.set('year_to', String(q.year_to));
  if (q.process) params.set('process', q.process);
  return params;
}

/** Search over the text of the SEI decision documents: exact terms, meaning, or both. */
export function fetchCadeDocuments(q: CadeDocumentQuery): Promise<CadeDocuments> {
  return apiFetch<CadeDocuments>(`/api/v1/cade/documents?${cadeDocumentParams(q)}`);
}

/** One process's milestone documents in CADE's public SEI, with its votes placed by date. */
export function fetchCadeSei(key: string): Promise<CadeSei> {
  return apiFetch<CadeSei>(`/api/v1/cade/sei/cases/${encodeURIComponent(key)}`);
}

export function fetchCadeJudicial(key: string): Promise<CadeJudicial> {
  return apiFetch<CadeJudicial>(`/api/v1/cade/judicial/cases/${encodeURIComponent(key)}`);
}

export function addCadeJudicialLink(
  key: string,
  body: CadeJudicialLinkIn,
): Promise<{ id: string; cnj_number: string }> {
  return apiFetch<{ id: string; cnj_number: string }>(
    `/api/v1/cade/judicial/cases/${encodeURIComponent(key)}/links`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export async function removeCadeJudicialLink(id: string): Promise<void> {
  await apiFetch<unknown>(`/api/v1/cade/judicial/links/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}
